import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ExternalLink, Copy, RefreshCw, Loader, User, Clock, DollarSign, TrendingDown, Search, X, MessageCircle, Youtube, Globe, Pill } from 'lucide-react';
import { useWalletUser } from '../../hooks/useWalletUser';
import { adminSupabase } from '../../services/supabaseService';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';

interface RuggedToken {
  mint: string;
  name: string | null;
  symbol: string | null;
  image_url: string | null;
  dev_wallet: string | null;
  dev_sell_time: string | null;
  dev_buy_time: string | null;
  dev_buy_tokens: number | null;
  market_cap_usd: number | null;
  price_usd: number | null;
  created_at: string | null;
  website: string | null;
  twitter: string | null;
  telegram: string | null;
  youtube: string | null;
}

interface RugTrackerProps {
  onClose?: () => void;
  isWidget?: boolean;
}

const RugTracker: React.FC<RugTrackerProps> = ({ onClose }) => {
  const walletUser = useWalletUser();
  const [ruggedTokens, setRuggedTokens] = useState<RuggedToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch rugged tokens with dev_sell_time
  const fetchRuggedTokens = useCallback(async () => {
    try {
      setError(null);
      
      console.log('🔍 Testing database connection and columns...');
      console.log('🔍 Using adminSupabase with service role key...');
      
      // First, let's test if we can access the table at all
      const { data: testData, error: testError } = await adminSupabase
        .from('new_tokens')
        .select('mint, name')
        .limit(1);
      
      if (testError) {
        console.error('❌ Basic table access failed:', testError);
        setError(`Database connection failed: ${testError.message}`);
        return;
      }
      
      console.log('✅ Basic table access successful, found', testData?.length, 'records');
      
      // Now test if dev_sell_time column exists
      console.log('🔍 Testing dev_sell_time column...');
      const { data: columnTestData, error: columnTestError } = await adminSupabase
        .from('new_tokens')
        .select('dev_sell_time')
        .limit(1);
      
      if (columnTestError) {
        console.error('❌ dev_sell_time column test failed:', columnTestError);
        setError(`Column dev_sell_time doesn't exist: ${columnTestError.message}`);
        return;
      }
      
      console.log('✅ dev_sell_time column exists');
      
      // Finally, try the full query
      console.log('🔍 Running full query...');
      const { data, error } = await adminSupabase
        .from('new_tokens')
        .select(`
          mint,
          name,
          symbol,
          image_url,
          dev_wallet,
          dev_sell_time,
          dev_buy_time,
          dev_buy_tokens,
          market_cap_usd,
          price_usd,
          created_at,
          website,
          twitter,
          telegram,
          youtube
        `)
        .not('dev_sell_time', 'is', null)
        .not('dev_wallet', 'is', null)
        .order('dev_sell_time', { ascending: false })
        .limit(100);

      if (error) {
        console.error('❌ Full query failed:', error);
        throw error;
      }

      console.log('✅ Full query successful, found', data?.length || 0, 'rugged tokens');
      setRuggedTokens(data || []);
      
    } catch (error) {
      console.error('❌ Error fetching rugged tokens:', error);
      setError(`Failed to load rugged tokens: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchRuggedTokens();
      setIsLoading(false);
    };

    loadData();
  }, [fetchRuggedTokens]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    await fetchRuggedTokens();
    setIsRefreshing(false);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(text);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Format currency
  const formatCurrency = (value?: number | null) => {
    if (!value || value === 0) return '$0';
    if (value < 0.01) return `$${value.toFixed(6)}`;
    if (value < 1) return `$${value.toFixed(4)}`;
    if (value < 1000) return `$${value.toFixed(2)}`;
    if (value < 1000000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${(value / 1000000).toFixed(1)}M`;
  };

  // Format address
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Filter tokens based on search
  const filteredTokens = ruggedTokens.filter(token => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      token.name?.toLowerCase().includes(search) ||
      token.symbol?.toLowerCase().includes(search) ||
      token.mint.toLowerCase().includes(search) ||
      token.dev_wallet?.toLowerCase().includes(search)
    );
  });

  if (isLoading) {
    return (
      <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="rug-tracker">
        <style>{globalWidgetTextStyles}</style>
        
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <Loader className="w-6 h-6 animate-spin text-[#24c35b]" />
            <span className="text-gray-600">Loading rugged tokens...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="rug-tracker">
      <style>{globalWidgetTextStyles}</style>
      <style>{`
        /* Hide number input arrows */
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
          <h2 className="text-lg font-bold text-black">Rug Tracker</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-50"
            title="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tokens, symbols, or developer wallets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-black placeholder-gray-500 text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b] hover:border-gray-300"
          />
        </div>
      </div>

      {/* Action Bar */}
      <div className="p-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-gray-600 text-sm">Tokens where developers have sold</span>
        </div>
        <button
          onClick={refreshData}
          disabled={isRefreshing}
          className="px-3 py-1.5 rounded-lg bg-[#24c35b]/10 border border-[#24c35b]/20 text-[#24c35b] text-sm hover:bg-[#24c35b]/20 transition-all disabled:opacity-50 flex items-center"
        >
          <RefreshCw size={12} className={`mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-3 py-3" style={{ background: 'white', borderRadius: 0, overflowX: 'visible' }}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {filteredTokens.length === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchTerm ? 'No rugged tokens found matching your search' : 'No rugged tokens found'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTokens.map((token) => (
              <div 
                key={token.mint} 
                className="p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all"
              >
                {/* Token Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {token.image_url ? (
                      <img
                        src={token.image_url}
                        alt={token.symbol || 'Token'}
                        className="w-10 h-10 rounded-full border border-white/10"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center border border-white/10">
                        <span className="text-xs text-gray-300">
                          {token.symbol?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-black">
                          {token.name || 'Unknown Token'}
                        </h3>
                        <span className="text-sm text-gray-500">
                          ({token.symbol || 'UNKNOWN'})
                        </span>
                        <div className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/40">
                          <div className="flex items-center space-x-1">
                            <TrendingDown className="w-3 h-3" />
                            <span>RUGGED</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {formatAddress(token.mint)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(token.mint)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <a
                          href={`https://solscan.io/token/${token.mint}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-red-400 font-medium">
                      DEV SOLD
                    </div>
                    <div className="text-xs text-gray-400">
                      {token.dev_sell_time && formatTimeAgo(token.dev_sell_time)}
                    </div>
                  </div>
                </div>

                {/* Token Stats */}
                <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                  <div>
                    <span className="text-gray-500">Market Cap:</span>
                    <span className="ml-2 text-black">
                      {formatCurrency(token.market_cap_usd)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Price:</span>
                    <span className="ml-2 text-black">
                      {formatCurrency(token.price_usd)}
                    </span>
                  </div>
                </div>

                {/* Developer Info */}
                {token.dev_wallet && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-medium text-red-400">Developer Wallet</span>
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xs text-gray-300 font-mono">
                        {formatAddress(token.dev_wallet)}
                      </span>
                      <button
                        onClick={() => copyToClipboard(token.dev_wallet!)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <a
                        href={`https://solscan.io/account/${token.dev_wallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>

                    {token.dev_buy_tokens && (
                      <div className="text-xs text-gray-400 mb-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Bought: {token.dev_buy_tokens.toLocaleString()} tokens
                        {token.dev_buy_time && (
                          <span className="ml-2">
                            ({formatTimeAgo(token.dev_buy_time)})
                          </span>
                        )}
                      </div>
                    )}

                    {token.dev_sell_time && (
                      <div className="text-xs text-red-400">
                        <TrendingDown className="w-3 h-3 inline mr-1" />
                        Sold: {formatTimeAgo(token.dev_sell_time)}
                      </div>
                    )}
                  </div>
                )}

                {/* Social Links - Exact TokenCard styling but 50% bigger */}
                {(token.website || token.twitter || token.telegram || token.youtube) && (
                  <div className="flex gap-1 mt-2">
                    {/* X.com Search */}
                    <a
                      href={`https://x.com/search?q=${token.mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                      title="Search token address on Twitter"
                    >
                      <Search size={15} className="text-white" />
                    </a>

                    {/* Pump.fun pill */}
                    <a
                      href={`https://pump.fun/coin/${token.mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                      title="View on Pump.fun"
                    >
                      <Pill size={15} className="text-white" />
                    </a>

                    {/* Twitter/X Official */}
                    {token.twitter && (
                      <a
                        href={token.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="Official Twitter"
                      >
                        <img 
                          src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                          alt="X" 
                          className="w-4 h-4 invert"
                        />
                      </a>
                    )}

                    {/* Telegram */}
                    {token.telegram && (
                      <a
                        href={token.telegram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white bg-[#0088cc] rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="Official Telegram"
                      >
                        <MessageCircle size={15} />
                      </a>
                    )}

                    {/* YouTube */}
                    {token.youtube && (
                      <a
                        href={token.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white bg-[#FF0000] rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="Official YouTube"
                      >
                        <Youtube size={15} />
                      </a>
                    )}

                    {/* Website */}
                    {token.website && (
                      <a
                        href={token.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white bg-gray-600 rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="Official Website"
                      >
                        <Globe size={15} />
                      </a>
                    )}

                    {/* Solscan */}
                    <a
                      href={`https://solscan.io/token/${token.mint}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                      title="View on Solscan"
                    >
                      <ExternalLink size={15} className="text-white" />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RugTracker;

// Widget factory function
export const createRugTrackerWidget = () => ({
  id: 'rug-tracker',
  name: 'Rug Tracker',
  title: 'Rug Tracker',
  type: 'rug-tracker-widget',
  content: <RugTracker />,
  size: { width: 600, height: 500 },
  minSize: { width: 400, height: 300 },
  maxSize: { width: 800, height: 700 },
  category: 'Analytics',
  glassMorphism: true,
  containerStyle: {
    backgroundColor: "rgba(12, 20, 26, 0.7)",
    backdropFilter: "blur(6px)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
}); 